import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/lib/auth';
import { 
  getConversations, 
  getConversationMessages, 
  sendMessage, 
  markConversationAsRead,
  subscribeToMessages,
  subscribeToConversations,
  createConversation,
  deleteMessage,
  deleteConversation
} from '@/lib/messaging';
import { supabase } from '@/lib/supabase';
import { uploadMedia } from '@/lib/cloudflare';
import type { Conversation, Message, User } from '@/types/database';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ConversationList } from './ConversationList';
import { Plus, ArrowLeft, Search, Loader2, X, MoreVertical, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MessagesPageProps {
  initialConversationId?: string | null;
  initialUserId?: string | null;
}

export function MessagesPage({ initialConversationId, initialUserId }: MessagesPageProps) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadConversations().then(() => {
      if (initialConversationId) {
        const conversation = conversations.find(c => c.id === initialConversationId);
        if (conversation) {
          setSelectedConversation(conversation);
        }
      }
    });

    const subscription = subscribeToConversations((conversation) => {
      setConversations(prev => {
        const index = prev.findIndex(c => c.id === conversation.id);
        if (index === -1) return [...prev, conversation];
        return [
          ...prev.slice(0, index),
          { ...prev[index], ...conversation },
          ...prev.slice(index + 1)
        ];
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [initialConversationId]);

  // Handle initial user
  useEffect(() => {
    const startConversationWithUser = async () => {
      if (!initialUserId || !user) return;

      try {
        setLoading(true);
        // Get user details
        const { data: userData, error: userError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', initialUserId)
          .single();

        if (userError) throw userError;

        // Check if conversation already exists
        const existingConversation = conversations.find(conv => 
          conv.participants?.some(p => p.user.id === initialUserId)
        );

        if (existingConversation) {
          setSelectedConversation(existingConversation);
        } else {
          // Create new conversation
          const conversation = await createConversation([initialUserId]);
          setConversations(prev => [conversation, ...prev]);
          setSelectedConversation(conversation);
        }
      } catch (error) {
        console.error('Error starting conversation:', error);
      } finally {
        setLoading(false);
      }
    };

    startConversationWithUser();
  }, [initialUserId, user, conversations]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
      markConversationAsRead(selectedConversation.id);
      
      const subscription = subscribeToMessages(selectedConversation.id, (message) => {
        setMessages(prev => [...prev, message]);
        scrollToBottom();
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [selectedConversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const searchUsers = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .or(`username.ilike.%${searchQuery}%,full_name.ilike.%${searchQuery}%`)
          .limit(10);

        if (error) throw error;
        setSearchResults(data || []);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setSearching(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const data = await getConversations();
      if (!data || data.length === 0) {
        setConversations([]);
      } else {
        setConversations(data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading conversations:', error);
      setLoading(false);
    }
  };

  const loadMessages = async (conversationId: string) => {
    try {
      const data = await getConversationMessages(conversationId);
      if (!data) {
        setMessages([]);
      } else {
        setMessages(data);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (content: string, mediaFile?: File) => {
    if (!selectedConversation || (!content.trim() && !mediaFile)) return;

    try {
      let mediaUrl;
      let mediaType;

      if (mediaFile) {
        mediaUrl = await uploadMedia(mediaFile);
        mediaType = mediaFile.type.startsWith('video/') ? 'video' : 'image';
      }

      const message = await sendMessage(
        selectedConversation.id,
        content,
        mediaUrl,
        mediaType as 'image' | 'video' | undefined
      );

      setMessages(prev => [...prev, message]);
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0 || creating) return;

    setCreating(true);
    try {
      const conversation = await createConversation(selectedUsers.map(u => u.id));
      setConversations(prev => [conversation, ...prev]);
      setSelectedConversation(conversation);
      setSelectedUsers([]);
      setSearchQuery('');
    } catch (error) {
      console.error('Error creating conversation:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteMessage(messageId);
      setMessages(prev => prev.filter(m => m.id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      await deleteConversation(selectedConversation.id);
      setConversations(prev => prev.filter(c => c.id !== selectedConversation.id));
      setSelectedConversation(null);
      setShowMenu(false);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-dark-paper">
      {selectedConversation ? (
        <div className="flex flex-col h-full">
          {/* Conversation header */}
          <div className="sticky top-0 flex items-center justify-between p-3 bg-white dark:bg-dark-paper border-b dark:border-dark-border shadow-sm z-10">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSelectedConversation(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-full transition-colors"
              >
                <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              </button>
              <div className="flex items-center gap-2">
                {selectedConversation.participants?.map((participant) => (
                  <img
                    key={participant.user.id}
                    src={participant.user.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${participant.user.full_name}`}
                    alt={participant.user.full_name}
                    className="w-8 h-8 rounded-full ring-2 ring-white dark:ring-dark-paper"
                  />
                ))}
                <div>
                  <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100">
                    {selectedConversation.participants?.map(p => p.user.full_name).join(', ')}
                  </h3>
                </div>
              </div>
            </div>

            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-elevated rounded-lg transition-colors"
              >
                <MoreVertical className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>

              {showMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-paper rounded-lg shadow-lg border dark:border-dark-border py-1 z-50">
                  <button
                    onClick={handleDeleteConversation}
                    className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete Conversation</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <MessageList
            messages={messages}
            currentUserId={user?.id}
            messagesEndRef={messagesEndRef}
            onDeleteMessage={handleDeleteMessage}
          />
          <MessageInput onSend={handleSendMessage} />
        </div>
      ) : (
        <>
          <div className="sticky top-0 p-3 border-b dark:border-dark-border bg-white dark:bg-dark-paper shadow-sm space-y-3 z-10">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Messages</h2>
            </div>
            
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users to start a conversation..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>

            {/* Selected users */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center gap-2 bg-royal-purple/10 dark:bg-royal-purple/20 text-royal-purple px-3 py-1 rounded-full"
                  >
                    <span className="text-sm font-medium">{user.full_name}</span>
                    <button
                      onClick={() => setSelectedUsers(prev => prev.filter(u => u.id !== user.id))}
                      className="p-1 hover:bg-royal-purple/20 dark:hover:bg-royal-purple/30 rounded-full transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                <button
                  onClick={handleCreateConversation}
                  disabled={creating}
                  className="flex items-center gap-2 px-3 py-1 bg-royal-purple text-white rounded-full hover:bg-royal-purple/90 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-3 h-3" />
                      <span>Start Chat</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Search results */}
            {searchQuery && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-dark-paper rounded-lg shadow-lg border dark:border-dark-border max-h-60 overflow-y-auto z-10">
                {searching ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 text-royal-purple animate-spin" />
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map((searchUser) => {
                    const isSelected = selectedUsers.some(u => u.id === searchUser.id);
                    return (
                      <button
                        key={searchUser.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedUsers(prev => prev.filter(u => u.id !== searchUser.id));
                          } else {
                            setSelectedUsers(prev => [...prev, searchUser]);
                          }
                        }}
                        className={cn(
                          "w-full flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-dark-elevated transition-colors",
                          isSelected && "bg-royal-purple/5 dark:bg-royal-purple/10 text-royal-purple"
                        )}
                      >
                        <img
                          src={searchUser.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${searchUser.full_name}`}
                          alt={searchUser.full_name}
                          className="w-10 h-10 rounded-full"
                        />
                        <div className="flex-1 text-left">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{searchUser.full_name}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">@{searchUser.username}</p>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No users found
                  </p>
                )}
              </div>
            )}
          </div>
          
          <ConversationList
            conversations={conversations}
            selectedId={selectedConversation?.id}
            onSelect={setSelectedConversation}
          />
        </>
      )}
    </div>
  );
}